 /**
 * MmsModeStrategy
 * 
 * Strategy for MMS (Model Management System) mode.
 * This mode displays hydraulic model assets (nodes, lines) instead of standard infrastructure.
 */

import React from 'react';
import {
	MapModeStrategy,
	Infrastructure,
	PopupData,
	FetchParams,
	MapModeContext,
	DigitalTwinContext,
} from './MapModeStrategy';

// Lazy import of the MMS Popup component
const MmsPopupComponent = React.lazy(() => import('../../../pages/Mms/Popup.jsx'));

class MmsModeStrategy implements MapModeStrategy {
	name = 'mms';
	displayName = 'MMS Lens';
	dragRotate = false;
	touchRotate = false;

	/**
	 * In MMS mode, hide all standard infrastructure icons.
	 * MMS uses its own layer system (ScatterplotLayer + PathLayer).
	 */
	filterInfrastructures(
		infras: Infrastructure[],
		context: MapModeContext,
		selectedTypes: string[]
	): Infrastructure[] {
		// Return empty array to hide normal infrastructure
		return [];
	}

	/**
	 * In MMS mode, no standard infrastructure is visible, so no opacities needed.
	 */
	calculateOpacities(
		infras: Infrastructure[],
		context: MapModeContext,
		digitalTwinContext?: DigitalTwinContext
	): Record<number, number> {
		// Return empty object since no standard infras are visible
		return {};
	}

	/**
	 * In MMS mode, asset clicks are handled by the MMS layers directly.
	 * This method won't be called for MMS assets, but we provide a fallback.
	 */
	async handleAssetClick(
		infra: Infrastructure,
		context: MapModeContext
	): Promise<PopupData> {
		// Fallback: return minimal data
		// MMS clicks are handled by useMmsLayers hook
		return {
			measurementeInfraestructureId: infra.measurementeInfraestructureId,
			measurementInfraestructureName: infra.measurementInfraestructureName,
			infrastructureTypeName: infra.infrastructureTypeName,
		};
	}

	/**
	 * Popup component for MMS mode (from existing MMS page)
	 */
	PopupComponent = MmsPopupComponent;

	/**
	 * In MMS mode, no special fetch params for standard infrastructure endpoint.
	 * MMS data is fetched separately via useMmsMapData hook.
	 */
	getFetchParams(context: MapModeContext): FetchParams {
		return {
			mode: 'mms',
		};
	}

	/**
	 * In MMS mode, there are no highlighted standard infrastructure assets.
	 */
	getHighlightedAssets(
		context: MapModeContext,
		digitalTwinContext?: DigitalTwinContext
	): Set<number> | null {
		return null;
	}

	/**
	 * Props for the MMS popup component.
	 * The popup expects MMS-specific props (mmsId, object, locationId, etc.)
	 */
	getPopupProps(
		infraInfo: PopupData,
		context: MapModeContext,
		additionalData?: any
	): Record<string, any> {
		return {
			mmsId: context.selectedMmsId || additionalData?.mmsId,
			object: infraInfo,
			locationId: context.selectedLocation,
			isActive: true,
			onClose: additionalData?.onClose,
			setAttributesValue: additionalData?.setAttributesValue || (() => {}),
			onDelete: additionalData?.onDelete || (() => {}),
			onRestore: additionalData?.onRestore || (() => {}),
			onUpdateAttributeValue: additionalData?.onUpdateAttributeValue || (() => {}),
			onMouseDown: undefined,
			isDragging: false,
			onClick: (e: any) => e.stopPropagation(),
			showDepthToggle: true,
			onShowDepthChange: additionalData?.onShowDepthChange || (() => {}),
		};
	}
}

// Export singleton instance
export const mmsModeStrategy = new MmsModeStrategy();
export default MmsModeStrategy;
